import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { touchesCanada } from "@/Helpers/touchesCanada";
import { DangerousGoodsComponent } from "@/Shared/DangerousGoods/CustomComponents/DangerousGoodsComponent";
import { dangerousGoodsErrorMessagesForEmergencyContact } from "@/Shared/DangerousGoods/Validators/dangerousGoodsErrorMessagesForEmergencyContact";
import { useLineItemsShipmentLimits } from "@/Shared/LineItems/Validators/useLineItemsShipmentLimits";
import BookShipmentNavigationFooter2 from "./CustomComponents/BookShipmentNavigationFooter2";
import BookShipmentScreenLayout from "./CustomComponents/BookShipmentScreenLayout";
import BookShipmentSmallSection from "./CustomComponents/BookShipmentSmallSection";
import type { EmergencyContactDetails } from "@freightsimple/generated-dashboard-openapi-client/models/EmergencyContactDetails";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SetEmergencyDetailsOperationRequest } from "@freightsimple/generated-dashboard-openapi-client/apis/ShipmentsApi";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { setCurrentScreenIsDirty } from "@/Slices/bookShipmentSlice";

interface BookShipmentDangerousGoodsScreenProps {
  shipment: PreBookingShipment;
  onComplete: () => void;
  nextIdentifier: string;
  onPrevious: () => Promise<void>;
}

export function BookShipmentDangerousGoodsScreen(
  props: BookShipmentDangerousGoodsScreenProps,
) {
  const [forceValidation, setForceValidation] = useState(false);
  const { shipment, onComplete, nextIdentifier, onPrevious } = props;
  const { shipmentId } = shipment;
  const dispatch = useDispatch();

  // Canadian TDG shipments also need the TDG-responsible person and any ERAP.
  const showCanadianTdgFields = touchesCanada(
    shipment.pickupLocation.address.countryCode,
    shipment.deliveryLocation.address.countryCode,
  );

  // The TDG-responsible name is a person, not the shipping business, so it
  // isn't defaulted — the customer enters who is certifying the declaration.
  const [emergencyContactDetails, setEmergencyContactDetails] =
    useState<EmergencyContactDetails>(
      shipment.emergencyContactDetails || {
        contactName: "",
        contactPhoneNumber: "",
        contactPhoneNumberExtension: "",
      },
    );

  // The bounds (name length, ERAP shape) come from the backend's shipment limits,
  // so the screen blocks on the same numbers the API enforces.
  const shipmentLimits = useLineItemsShipmentLimits();
  const emergencyContactDetailsErrors =
    dangerousGoodsErrorMessagesForEmergencyContact(
      emergencyContactDetails,
      showCanadianTdgFields,
      shipmentLimits,
    );
  const shipmentsApi = useShipmentsApi();

  function isValid() {
    const anyErrorMessages = [
      emergencyContactDetailsErrors.contactName,
      emergencyContactDetailsErrors.contactPhoneNumber,
      emergencyContactDetailsErrors.erapPlanNumber,
      emergencyContactDetailsErrors.erapPhoneNumber,
      emergencyContactDetailsErrors.tdgResponsibleName,
    ].some((e) => e !== undefined);

    return !anyErrorMessages;
  }

  async function nextIsValid() {
    setForceValidation(true);
    if (isValid()) {
      const request: SetEmergencyDetailsOperationRequest = {
        setEmergencyDetailsRequest: {
          shipmentId,
          emergencyDetails: emergencyContactDetails as EmergencyContactDetails,
        },
      };

      // TODO: Move this to a different API
      await shipmentsApi.setEmergencyDetails(request);
      onComplete();
      return true;
    } else {
      return false;
    }
  }

  function onUpdateEmergencyContactDetails(
    update: Partial<EmergencyContactDetails>,
  ) {
    dispatch(setCurrentScreenIsDirty());
    setEmergencyContactDetails((prev) => ({
      ...prev,
      ...update,
    }));
  }

  return (
    <div id="dangerous-goods-section">
      {/* Same frame as the address and broker steps: `underlined` is the
          Cargo-derived control look the rest of the booking flow uses, and the
          layout keeps the heading, panel and footer on one column. */}
      <ControlPresentationProvider presentation="underlined">
        <BookShipmentScreenLayout
          pageHeading="Dangerous Goods"
          pageSubheading={
            showCanadianTdgFields
              ? "24-hour emergency contact details and the person responsible for the TDG declaration are required for shipping dangerous goods"
              : "24-hour emergency contact details are required for shipping dangerous goods"
          }
        >
          <BookShipmentSmallSection
            header={undefined}
            description={undefined}
            index={1}
          >
            <DangerousGoodsComponent
              emergencyContactDetails={emergencyContactDetails}
              onUpdateEmergencyContactDetails={onUpdateEmergencyContactDetails}
              forceValidation={forceValidation}
              emergencyContactDetailsErrors={emergencyContactDetailsErrors}
              showCanadianTdgFields={showCanadianTdgFields}
            />
          </BookShipmentSmallSection>
          <BookShipmentNavigationFooter2
            next="Next"
            onPrevious={onPrevious}
            nextUrl={`/book/${nextIdentifier}?shipmentId=${shipmentId}`}
            nextIsValid={nextIsValid}
            nextIsEnabled={() => true}
            nextIdentifier={nextIdentifier}
            hidePreviousButton={false}
          />
        </BookShipmentScreenLayout>
      </ControlPresentationProvider>
    </div>
  );
}
